<template>
  <input
    v-if="type === 'password' || type === 'email'"
    v-model="inputValue"
    v-bind="$attrs"
    :data-id="$attrs.label || $attrs.name || $attrs.object?.label || $attrs.field || $attrs.id"
    :type="type === 'number' ? 'text' : type"
    @keydown="keyDown"
    :minlength="minSymbols"
    :required="required"
    @input="checkValidation"
    autocomplete="off"
    class="bg-white"
  />
  <input
    v-else
    v-model="inputValue"
    :readonly="readonly"
    v-bind="$attrs"
    :data-id="$attrs.label || $attrs.name || $attrs.object?.label || $attrs.object?.name || $attrs.field || $attrs.id"
    :type="type === 'number' ? 'text' : type"
    @keydown="keyDown"
    :minlength="minSymbols"
    :required="required"
    v-mask="type === 'tel' ? '###-###-####' : mask"
    @input="checkValidation"
    autofocus
    :pattern="pattern"
    autocomplete="temp"
  />
</template>

<script>
import { VueMaskDirective } from 'v-mask';

function getCaretPosition(ctrl) {
  // IE < 9 Support
  if (document.selection) {
    ctrl.focus();
    const range = document.selection.createRange();
    const rangelen = range.text.length;
    range.moveStart('character', -ctrl.value.length);
    const start = range.text.length - rangelen;
    return { start, end: start + rangelen };
  }
  // IE >=9 and other browsers
  else if (ctrl.selectionStart || ctrl.selectionStart == '0') {
    return { start: ctrl.selectionStart, end: ctrl.selectionEnd };
  }
  return { start: 0, end: 0 };
}

function setCaretPosition(ctrl, start, end = 0) {
  // IE >= 9 and other browsers
  if (ctrl.setSelectionRange) {
    ctrl.focus();
    ctrl.setSelectionRange(start, end);
  }
  // IE < 9
  else if (ctrl.createTextRange) {
    const range = ctrl.createTextRange();
    range.collapse(true);
    range.moveEnd('character', end);
    range.moveStart('character', start);
    range.select();
  }
}

export default {
  name: 'AdvancedInput',
  directives: { mask: VueMaskDirective },
  props: {
    // eslint-disable-next-line vue/require-prop-types
    pattern: {
      type: String,
      default: null
    },
    required: {
      type: Boolean,
      default: false
    },
    value: {
      default: ''
    },
    mask: String,
    type: {
      type: String,
      validator(value) {
        if (!['text', 'number', 'tel', 'email', 'password'].includes(value)) {
          console.error('Invalid value for prop `type`');
          return false;
        }
        return true;
      },
      default: 'text'
    },
    readonly: {
      type: Boolean
    },
    decimal: {
      type: Boolean,
      default: false
    },
    precision: {
      type: Number,
      default: 2
    },
    minSymbols: {
      type: Number,
      default: -Infinity
    },
    maxSymbols: {
      type: Number,
      default: Infinity
    },
    min: {
      type: Number,
      default: -Infinity
    },
    max: {
      type: Number,
      default: Infinity
    },
    step: {
      type: Number,
      default: 1
    },
    validator: {
      type: Function,
      required: false
    }
  },
  data() {
    return {
      inputValue: `${this.value}`
    };
  },
  updated() {
    if (this.type !== 'number') {
      this.inputValue = this.value;
    }
  },
  computed: {
    numberValue() {
      if (this.inputValue === '-' || this.inputValue === '.' || this.inputValue === '-.') {
        return 0;
      }
      const numberValue = +this.inputValue;
      return isNaN(numberValue) ? null : numberValue;
    }
  },
  watch: {
    inputValue: {
      handler(value) {
        if (this.type === 'number') {
          if (typeof value === 'string') {
            const isMinus = value === '-';
            const isDot = value === '.';
            const isDotMinus = value === '.-';
            const willBeTrimmed = value.indexOf('.') !== -1 && value.endsWith('0');
            if (isMinus || isDot || isDotMinus || willBeTrimmed) {
              // Предотвратить обновление значения если ввёдённое значение является промежуточным для ввода числа
              return;
            }
          }

          const numberValue = value; // Превращаем значение в число

          const realValue = value === '' || isNaN(numberValue) ? null : numberValue; // Не превращать пустую строку в 0

          this.$emit('input', realValue);
        } else {
          this.$emit('input', value);
        }
      },
      immediate: true
    }
  },
  methods: {
    checkValidation(input) {
      if (this.validator) {
        const message = this.validator(input.target.value);
        if (input.target.validity.valid) {
          input.target.setCustomValidity(message);
        } else if (!message) {
          input.target.setCustomValidity('');
        }
      }
    },
    keyDown(e) {
      if (!e.key) {
        return;
      }
      const isServiceKey = e.key.length > 1; // Нажата сервисная клавиша
      const isCombination = (e.ctrlKey || e.altKey) && !isServiceKey; // Нажата комбинация клавиш
      const isSymbol = !isServiceKey && !isCombination; // Нажата клавиша с печатаемым символом
      const isNumber = isSymbol && !isNaN(+e.key); // Нажата клавиша с цифрой
      const isDot = e.key === '.' || e.key === ','; // Нажата точка или запятая
      const isMinus = e.key === '-'; // Нажат минус

      // console.log(
      //   'combination', isCombination,
      //   '\nservice', isServiceKey,
      //   '\nsymbol', isSymbol,
      //   '\nnumber', isNumber,
      //   '\ndot', isDot,
      // );

      const prevent = () => e.preventDefault();
      if (this.type === 'number') {
        if (isNumber || isDot || isMinus) {
          // Если нажато число, точка или минус
          if (
            (!this.decimal && isDot) || // Если ввод десятичных запрещён и нажата точка
            (this.min >= 0 && isMinus) // Если ввод отрицательных запрещён и нажат минус
          ) {
            return prevent();
          }

          const caretPosition = getCaretPosition(e.target); // Получаем положение курсора в инпуте

          if (isMinus && caretPosition.start !== 0) {
            // Если минус вводится не в начале числа
            return prevent();
          }

          const possibleValueCharArray = `${this.inputValue}`.split(''); // Массив символов текущего значения инпута

          const charToAdd = isDot ? '.' : e.key; // Какой символ добавить

          possibleValueCharArray.splice(caretPosition.start, caretPosition.end - caretPosition.start, charToAdd); // Удаляем выделенные символы и вставляем нажатый символ
          const possibleValue = possibleValueCharArray.join('').replace(/^0+/, '0');

          const possibleNumberValue = +possibleValue;

          const isValidNumber = !isNaN(possibleNumberValue) && Math.abs(possibleNumberValue) !== Infinity; // Если в после ввода не выходит NaN // Если после ввода не выходит [-]Infinity

          const becomeMinus = possibleValue === '-'; // Если введён только минус
          const endsWithDot = possibleValue.endsWith('.'); // Если заканчивается точкой
          const goodLength = possibleValue.length <= this.maxSymbols; // Если длинна результирующего числа не больше максимальной
          const goodPrecision = !possibleValue.includes('.') || possibleValue.split('.')[1].length <= this.precision; // Вводится десятичное число // Если точность не превышает заданную

          if (
            (becomeMinus || // Если выходит только минус
              endsWithDot || // Или заканчивается точкой
              isValidNumber) && // Или является валидным числом
            goodLength && // И длина не превышает лимит
            goodPrecision // И точность не превышает заданную
          ) {
            if (isValidNumber && !endsWithDot && possibleNumberValue > this.max) {
              // Если результирующее число выходит больше максимального
              this.inputValue = `${this.max}`;
            } else if (isValidNumber && !endsWithDot && possibleNumberValue < this.min) {
              // Если результирующее число выходит меньше минимального
              this.inputValue = `${this.min}`;
            } else {
              this.inputValue = possibleValue;
            }

            this.$nextTick(() => {
              let factor = 1;

              if (possibleValue.startsWith('.')) {
                factor = 2; // Если значение начинается с точки подвинуть курсор на 2 позиции
              } else if (possibleValue.startsWith('-.')) {
                factor = 3; // Если значение начинается с минуса и точки подвинуть курсор на 3 позиции
              }
              const newCaretPosition = caretPosition.start + factor;
              setCaretPosition(e.target, newCaretPosition, newCaretPosition);
            });
          }
        } else if (isServiceKey) {
          // Если нажата сервисная клавиша
          switch (e.keyCode) {
            case 38: // Up
              this.up(e); // Увеличить значение
              break;
            case 40: // Down
              this.down(e); // Уменьшить значение
              break;
            default:
              return null;
          }
        } else if (isCombination) {
          // Если нажата комбинация клавиш
          // Ничего не делать
          return null;
        }
        return prevent();
      } else if (this.type === 'text' || this.type === 'password') {
        const possibleValue = `${this.inputValue}`.split('').join('');
        if (isServiceKey) {
          // Если нажата сервисная клавиша
          switch (e.keyCode) {
            case 38: // Up
              this.up(e); // Увеличить значение
              break;
            case 40: // Down
              this.down(e); // Уменьшить значение
              break;
            default:
              return null;
          }
        }
        if (possibleValue.length >= this.maxSymbols) {
          return prevent();
        }
      } else if (this.type === 'tel') {
        const possibleValue = `${this.inputValue}`.split('').join('');
        if (!isServiceKey) {
          if (!isNumber || possibleValue.length >= 12) {
            return prevent();
          }
          if (possibleValue.length === 2 || possibleValue.length === 6) {
            this.inputValue = `${this.inputValue}${e.key}-`;
            return prevent();
          }
        } else {
          // Если нажата сервисная клавиша
          switch (e.keyCode) {
            case 38: // Up
              this.up(e); // Увеличить значение
              break;
            case 40: // Down
              this.down(e); // Уменьшить значение
              break;
            default:
              return null;
          }
        }
      }
    },
    up() {
      const newVal = this.numberValue + this.step;
      if (newVal > this.max) {
        this.inputValue = `${this.max}`;
      } else {
        this.inputValue = `${newVal}`;
      }
    },
    down() {
      const newVal = this.numberValue - this.step;
      if (newVal < this.min) {
        this.inputValue = `${this.min}`;
      } else {
        this.inputValue = `${newVal}`;
      }
    }
  }
};
</script>
